import { useFullstoryElement } from '@hooks/useFullstory';
import { FS_UNMASK, FullStoryElements, FullStoryType } from '@utils/fullstory';
import { useWorkflow } from '@utils/fullstory/context';
import cx from 'classnames';
import { forwardRef, HTMLProps } from 'react';
import {
  aiStyles,
  dangerGhostStyles,
  dangerStyles,
  errorSystemDialogInverseStyle,
  errorSystemDialogStyle,
  ghostStyles,
  infoSystemDialogInverseStyle,
  infoSystemDialogStyle,
  outlineStyles,
  quietStyles,
  solidStyles,
  successSystemDialogInverseStyle,
  successSystemDialogStyle,
  warningSystemDialogInverseStyle,
  warningSystemDialogStyle,
} from './style.css';

interface ButtonTheme {
  alert?: Array<string>;
  inverseAlert?: Array<string>;
  lockBannerAlert?: Array<string>;
  infoSystemDialog?: Array<string>;
  infoSystemDialogInverse?: Array<string>;
  errorSystemDialog?: Array<string>;
  errorSystemDialogInverse?: Array<string>;
  warningSystemDialog?: Array<string>;
  warningSystemDialogInverse?: Array<string>;
  successSystemDialog?: Array<string>;
  successSystemDialogInverse?: Array<string>;
}

export type ButtonOption = keyof ButtonTheme;

type CommonHTMLProps =
  | 'autoFocus'
  | 'children'
  | 'className'
  | 'disabled'
  | 'hidden'
  | 'id'
  | 'label'
  | 'name'
  | 'onBlur'
  | 'onClick'
  | 'onKeyUp'
  | 'onMouseEnter'
  | 'onMouseLeave'
  | 'readOnly'
  | 'style'
  | 'tabIndex'
  | 'title'
  | 'href'
  | 'type'
  | 'width';

export interface ButtonProps
  extends Pick<HTMLProps<HTMLButtonElement>, CommonHTMLProps> {
  name: string;
  option?: ButtonV2Option | ButtonOption;
  as?: 'button' | 'span' | 'div';
  show?: boolean;
  /** Disables svg color style on hover */
  noIconColorOnHover?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

interface ButtonV2Theme {
  default: Record<string, string>;
  ghost?: Record<string, string>;
  outline?: Record<string, string>;
  solid?: Record<string, string>;
  danger?: Record<string, string>;
  dangerGhost?: Record<string, string>;
  ai?: Record<string, string>;
  quiet?: Record<string, string>;
}

type ButtonV2Option = keyof ButtonV2Theme;

// Combine the interfaces using intersection
// ts-unused-exports:disable-next-line
export interface ExtendedButtonProps extends ButtonProps, FullStoryType {}

export const Button = forwardRef<HTMLElement, ExtendedButtonProps>(
  (originalProps, ref) => {
    const {
      as = 'button',
      show = true,
      option = 'default',
      fsType,
      onMouseEnter,
      ...props
    } = originalProps;
    const { getFsComponentProps } = useFullstoryElement();
    const { id } = useWorkflow();

    const El = as as anyOk;

    const stylesMaps: { [key: string]: string | null } = {
      solid: solidStyles,
      ghost: ghostStyles,
      danger: dangerStyles,
      dangerGhost: dangerGhostStyles,
      outline: outlineStyles,
      ai: aiStyles,
      quiet: quietStyles,
      infoSystemDialog: infoSystemDialogStyle,
      errorSystemDialog: errorSystemDialogStyle,
      warningSystemDialog: warningSystemDialogStyle,
      successSystemDialog: successSystemDialogStyle,
      infoSystemDialogInverse: infoSystemDialogInverseStyle,
      errorSystemDialogInverse: errorSystemDialogInverseStyle,
      warningSystemDialogInverse: warningSystemDialogInverseStyle,
      successSystemDialogInverse: successSystemDialogInverseStyle,
    };
    const styles = stylesMaps[option] || outlineStyles;

    const handleMouseEnter = (
      e: React.MouseEvent<HTMLElement, MouseEvent>,
    ): void => {
      // Call original onMouseEnter if it exists.
      if (onMouseEnter) {
        onMouseEnter(e as anyOk);
      }

      // Only set title if not explicitly provided and text is truncated.
      if (!props.title && e.currentTarget) {
        const element = e.currentTarget;
        const isTextTruncated = element.scrollWidth > element.clientWidth;
        if (isTextTruncated && element.textContent) {
          element.setAttribute('title', element.textContent);
        }
      }
    };

    return (
      <>
        {show ? (
          <El
            {...props}
            {...getFsComponentProps({
              name: props?.name,
              element: FullStoryElements.BUTTON,
              parent: id,
              type: fsType ?? props?.type,
            })}
            className={cx(styles, FS_UNMASK, props?.className)}
            ref={ref as React.Ref<HTMLElement>}
            type={props?.type || 'button'}
            onMouseEnter={handleMouseEnter}
          >
            {props.children}
          </El>
        ) : null}
      </>
    );
  },
);
